/**
 * @fileoverview Provider de capacités backoffice mocké.
 *
 * Frontend-only : il masque les éléments que l'agent courant ne devrait pas
 * voir, mais ne garantit aucune sécurité. Le backend refusera toujours les
 * actions non autorisées via 403 lors de l'intégration réelle.
 */

import { createContext, useContext, type ReactNode } from 'react';
import { CURRENT_AGENT, type Capability, type CurrentAgent } from '../../data/backoffice.ts';

interface CapabilityContextValue {
  user: CurrentAgent;
  hasCapability: (capability: Capability) => boolean;
}

const CapabilityContext = createContext<CapabilityContextValue | null>(null);

export interface CapabilityProviderProps {
  children: ReactNode;
}

export interface CapabilityGuardProps {
  /** Capacité requise. Jamais de rôle minimum dans ce projet. */
  capability?: Capability;
  children: ReactNode;
  fallback?: ReactNode;
}

export function CapabilityProvider({ children }: CapabilityProviderProps): JSX.Element {
  const value: CapabilityContextValue = {
    user: CURRENT_AGENT,
    hasCapability: (capability) => CURRENT_AGENT.capabilities.includes(capability),
  };

  return <CapabilityContext.Provider value={value}>{children}</CapabilityContext.Provider>;
}

export function useCurrentUser(): CapabilityContextValue {
  const context = useContext(CapabilityContext);
  if (!context) {
    throw new Error('useCurrentUser must be used inside CapabilityProvider');
  }
  return context;
}

/**
 * Masque UX-only basé sur une capacité atomique.
 *
 * @param props - Capacité requise, contenu et fallback.
 * @returns Les enfants si la capacité est présente, sinon le fallback.
 */
export function CapabilityGuard({
  capability,
  children,
  fallback = null,
}: CapabilityGuardProps): JSX.Element | null {
  const { hasCapability } = useCurrentUser();
  if (!capability) return <>{children}</>;
  return hasCapability(capability) ? <>{children}</> : <>{fallback}</>;
}
